'use client'

import { useMemo } from 'react'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { ProductOptions } from './ProductOptions'
import { ViPoints } from './ViPoints'
import { Ratings } from './Ratings'
import { ShippingStatus } from './ShippingStatus'
import { CreatorsLinkList } from '@/General/CreatorsLinkList'
import { AddToWishlist } from '../AddToWishlist'
import { Price } from '@/General/Price'
import { useStore } from '@/store'
import { formatDate } from '@/date'
import { isPreorderReleaseTodayOrFuture } from '@/lib/formatProductType'
import type { GroupChildProductForCart } from '@/lib/groupChildCart'
import type { RegionalBrandCode } from '@/lib/seo/marketUrls'

export const ProductBuy = ({
  productId,
  name,
  productSlug = '',
  options,
  inStock,
  productType,
  creators,
  averageRating,
  reviewsCount,
  vanishingIncExclusive,
  isMasterclass = false,
  isFree = false,
  ebookBundleOption = null,
  discontinued = false,
  isPreorder = false,
  preorderReleaseDate = null,
  stockMessage = null,
  hidePrice = false,
  restockDateDisplay = null,
  optionEmailNotification = true,
  hasDownloads = false,
  showChildPurchaseHint = false,
  groupChildProducts,
  addToCartButtonText = null,
  addToCartButtonLinkOverride = null,
  brandCode = null,
}: ProductBuyProps) => {
  const selectedOptionId = useStore(s => s.selectedOptionId)
  const displayOption = useMemo(() => {
    if (options.length === 0) return null
    if (options.length === 1) return options[0]
    return options.find(o => o.id === selectedOptionId) ?? options[0]
  }, [options, selectedOptionId])
  const displayOptionHasDiscount =
    displayOption?.salePrice != null &&
    displayOption.salePrice > 0 &&
    displayOption.salePrice < displayOption.price

  const isDiscontinuedOOS = discontinued && !inStock && !isPreorder
  const allOptionsOutOfStock =
    options.length > 0 && options.every(option => !option.inStock)
  const preorderLaunchLong =
    preorderReleaseDate && isPreorderReleaseTodayOrFuture(preorderReleaseDate)
      ? formatDate(preorderReleaseDate)
      : null
  const isUkStorefront = brandCode === 'VANISHING_INC_UK'

  return (
    <div className="w-full md:w-[400px] md:rounded-md md:shadow-md md:px-4 md:p-6 flex flex-col bg-white">
      <h2 className="text-lg md:text-xl lg:text-2xl font-bold hidden md:block">
        {name}
      </h2>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
        <div className="text-gray-500 text-sm sm:text-base flex-1 hidden md:block">
          {productType}
          {(creators?.length ?? 0) > 0 && (
            <>
              {' '}
              by <CreatorsLinkList creators={creators} />
            </>
          )}
        </div>
        <div className="text-gray-500 text-sm flex items-center">
          {stockMessage && <span>{stockMessage}</span>}
          {!inStock && !stockMessage && !isPreorder && (
            <>
              <XMarkIcon className="w-4 h-4 mr-[1px] stroke-3 stroke-gray-400" />{' '}
              {isDiscontinuedOOS ? 'Possibly discontinued' : 'Out of stock'}
            </>
          )}
        </div>
      </div>

      {isPreorder && preorderLaunchLong && (
        <p className="text-gray-600 text-sm mt-1 md:hidden">
          Launches {preorderLaunchLong}
        </p>
      )}

      {!inStock && restockDateDisplay && !isPreorder && (
        <p className="text-gray-600 mt-1">{restockDateDisplay}</p>
      )}

      {isDiscontinuedOOS && (
        <p className="text-gray-600 mt-1">
          Click the button below to be emailed if it becomes available again.
        </p>
      )}

      {/* Pricing display */}
      {!hidePrice && options.length > 0 && displayOption && (
        <div className="my-2 md:my-3">
          {options.length > 1 ? (
            <div className="space-y-1" aria-live="polite">
              <Price
                currencySymbol={displayOption.currencySymbol || ''}
                currencyCode={displayOption.currencyCode}
                price={displayOption.price}
                salePrice={displayOption.salePrice}
                priceIncVat={displayOption.priceIncVat}
                salePriceIncVat={displayOption.salePriceIncVat}
                taxType={displayOption.taxType}
                size="xl"
                brandCode={brandCode}
                amountClassName={
                  displayOptionHasDiscount ? 'text-vi-red' : 'text-black'
                }
              />
              <div className="text-gray-500 text-sm">
                {allOptionsOutOfStock && !isPreorder && optionEmailNotification
                  ? 'Select an option to be notified about'
                  : 'Multiple options available - select below'}
              </div>
            </div>
          ) : (
            <Price
              currencySymbol={displayOption.currencySymbol || ''}
              currencyCode={displayOption.currencyCode}
              price={displayOption.price}
              salePrice={displayOption.salePrice}
              priceIncVat={displayOption.priceIncVat}
              salePriceIncVat={displayOption.salePriceIncVat}
              taxType={displayOption.taxType}
              size="xl"
              brandCode={brandCode}
              amountClassName={
                displayOptionHasDiscount ? 'text-vi-red' : 'text-black'
              }
            />
          )}
        </div>
      )}

      {isUkStorefront && displayOption && !displayOption.needsShipping && (
        <div className="my-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          Delivered digitally. Prices shown include VAT where applicable. See
          our returns policy for details.
        </div>
      )}

      {showChildPurchaseHint && (
        <p className="text-gray-600 text-sm mt-2">
          {options.length > 0
            ? 'This set is not available as a bundle right now — individual volumes are in stock below.'
            : 'Available as individual items below.'}
        </p>
      )}

      <ProductOptions
        productId={productId}
        productSlug={productSlug}
        productName={name || ''}
        productType={productType}
        options={options}
        productInStock={inStock}
        isMasterclass={isMasterclass}
        isFree={isFree}
        ebookBundleOption={ebookBundleOption}
        isPreorder={isPreorder}
        preorderReleaseDate={preorderReleaseDate}
        showEmailNotification={optionEmailNotification}
        hasDownloads={hasDownloads}
        groupChildProducts={groupChildProducts}
        addToCartButtonText={addToCartButtonText}
        addToCartButtonLinkOverride={addToCartButtonLinkOverride}
        brandCode={brandCode}
      />

      {displayOption?.needsShipping && <ShippingStatus />}
      <div className="flex flex-col sm:flex-row sm:items-center md:py-4 gap-2 sm:gap-0">
        <div className="flex-1">
          <Ratings averageRating={averageRating} reviewsCount={reviewsCount} />
        </div>
        <div className="hidden md:block">
          {vanishingIncExclusive && (
            <img
              src="https://vinc.gumlet.io/pics/icons/vi-exclusive-1.png?w=100&dpr=2"
              alt="VI Exclusive"
              className="w-24"
            />
          )}
        </div>
      </div>
      {!isFree && (
        <div className="text-gray-500 text-sm space-y-2 border-b pb-4 md:border-b-0 md:pb-0 mt-auto">
          <div className="text-center md:text-left pt-2 md:pt-0 flex flex-col gap-2">
            <AddToWishlist
              productId={productId}
              productName={name}
              variant="text"
            />
            <ViPoints
              productOptions={options.map(option => ({
                id: option.id,
                viPoints: option.viPoints,
              }))}
            />
          </div>
        </div>
      )}
    </div>
  )
}

interface ProductBuyProps {
  productId: number
  name: string
  productSlug?: string
  ebookBundleOption?: {
    physicalOptionId: number
    downloadOptionId: number
  } | null
  discontinued?: boolean
  isPreorder?: boolean
  preorderReleaseDate?: string | null
  stockMessage?: string | null
  hidePrice?: boolean
  restockDateDisplay?: string | null
  /** When false, skip email-notify for the parent bundle (e.g. no parent-level options). */
  optionEmailNotification?: boolean
  hasDownloads?: boolean
  /** Parent PDP: bundle not purchasable but at least one child is in stock — prompt scroll to #individual. */
  showChildPurchaseHint?: boolean
  groupChildProducts?: GroupChildProductForCart[] | null
  addToCartButtonText?: string | null
  addToCartButtonLinkOverride?: string | null
  brandCode?: RegionalBrandCode | null
  options: {
    id: number
    description: string
    price: number
    salePrice?: number | null
    priceIncVat?: number | null
    salePriceIncVat?: number | null
    taxType?: number | null
    currencySymbol: string
    currencyCode?: string | null
    inStock: boolean
    productType: string
    needsShipping: boolean
    viPoints: number
  }[]
  inStock: boolean
  vanishingIncExclusive: boolean
  productType: string
  creators?:
    | ({
        urlStub?: string | null
        name?: string | null
      } | null)[]
    | null
    | undefined
  averageRating?: number
  reviewsCount?: number
  isMasterclass?: boolean
  isFree?: boolean
}
